import React from "react";
import { useNavigate } from "react-router-dom";
import { SvgIcons, PreLoading } from "@tripian/react";
import Model, { Providers } from "@tripian/model";
import moment from "moment";

import useTranslate from "../../../hooks/useTranslate";
import viatorLogo from "../img/favicon-viator.png";
import "./style.scss";

const getCurrencySymbol = (currencyCode: string): string => {
    try {
        return new Intl.NumberFormat('en', { style: 'currency', currency: currencyCode })
            .formatToParts(0)
            .find(part => part.type === 'currency')?.value || currencyCode;
    } catch {
        return currencyCode;
    }
};

interface RecommendToursProps {
    viatorToursCatalog?: Providers.Viator.CatalogGroup[];
    loadingViatorTourCatalog?: boolean;
    victoryEventsCatalog?: Providers.Victory.CatalogGroup[];
    loadingVictoryEventCatalog?: boolean;
    tourApiProducts?: Model.TourSearchProduct[];
    loadingTourApi?: boolean;
    title?: string;
    provider?: "viator" | "victory" | "tourApi";
    onTourClick?: (provider: number, tourId: string, additionalData?: any) => void;
    cityId?: number;
    cityName?: string;
}

const RecommendTours: React.FC<RecommendToursProps> = ({
    viatorToursCatalog,
    loadingViatorTourCatalog = false,
    victoryEventsCatalog,
    loadingVictoryEventCatalog = false,
    tourApiProducts,
    loadingTourApi = false,
    title,
    provider = "viator",
    onTourClick,
    cityId,
    cityName
}) => {
    const navigate = useNavigate();
    const { t } = useTranslate();

    // Handle View All button click - navigate to DetailPoiAndTour page with tours-tickets tab
    const handleViewAll = () => {
        if (cityId) {
            navigate(`/things-to-do/${cityId}?tab=tours-tickets&provider=${provider}`);
        }
    };

    // Get all Viator tours from all categories and limit to 6
    const viatorTours = React.useMemo(() => {
        if (!viatorToursCatalog || provider !== "viator") return [];

        const allTours: Providers.Viator.Product[] = [];
        viatorToursCatalog.forEach(catalog => {
            allTours.push(...catalog.items);
        });

        // Remove duplicates based on productCode
        const uniqueTours = allTours.filter((tour, index, self) =>
            index === self.findIndex((t) => t.productCode === tour.productCode)
        );

        return uniqueTours.slice(0, 6);
    }, [viatorToursCatalog, provider]);

    // Get all Victory events from all categories and limit to 8
    const victoryEvents = React.useMemo(() => {
        if (!victoryEventsCatalog || provider !== "victory") return [];

        const allEvents: Providers.Victory.Event[] = [];
        victoryEventsCatalog.forEach(catalog => {
            allEvents.push(...catalog.items);
        });

        // Remove duplicates based on id and filter out events with available_count = 0
        const uniqueEvents = allEvents.filter((event, index, self) =>
            index === self.findIndex((e) => e.id === event.id) &&
            (event.available_count === undefined || event.available_count > 0)
        );

        return uniqueEvents.slice(0, 8);
    }, [victoryEventsCatalog, provider]);

    // Get Tour API products, deduplicated and limited to 6
    const tourApiTours = React.useMemo(() => {
        if (!tourApiProducts || provider !== "tourApi") return [];

        const uniqueTours = tourApiProducts.filter((tour, index, self) =>
            index === self.findIndex((t) => t.productId === tour.productId)
        );

        return uniqueTours.slice(0, 6);
    }, [tourApiProducts, provider]);

    // Get provider info based on provider_id
    const getProviderInfo = (providerId: number): { name: string; logo?: string } => {
        switch (providerId) {
            case Model.PROVIDER_ID.VIATOR:
                return { name: "Viator", logo: viatorLogo };
            case Model.PROVIDER_ID.GYG:
                return { name: "GetYourGuide" };
            case Model.PROVIDER_ID.CIVITATIS:
                return { name: "Civitatis" };
            case Model.PROVIDER_ID.VICTORY:
                return { name: "Victory" };
            default:
                return { name: "Tour" };
        }
    };

    // Format time from occurs_at_local - use parseZone to preserve original timezone
    const formatEventTime = (occursAtLocal: string | undefined): string => {
        if (!occursAtLocal) return "";
        const startTime = moment.parseZone(occursAtLocal);
        return `${startTime.format("h:mm A")} Onwards`;
    };

    const isLoading = provider === "tourApi" ? loadingTourApi : provider === "viator" ? loadingViatorTourCatalog : loadingVictoryEventCatalog;
    const hasItems = provider === "tourApi"
        ? tourApiTours && tourApiTours.length > 0
        : provider === "viator"
            ? viatorTours && viatorTours.length > 0
            : victoryEvents && victoryEvents.length > 0;

    if (isLoading) {
        return (
            <div className="w-full py-12 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center justify-between mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 syne-fm">
                            {title || t("valai.recommendedTours")}
                        </h2>
                    </div>
                    <div className="flex items-center justify-center py-12">
                        <PreLoading />
                    </div>
                </div>
            </div>
        );
    }

    if (!hasItems) {
        return null;
    }

    return (
        <div className="w-full py-12 px-4">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <h2 className="text-2xl font-semibold text-gray-900 syne-fm">
                        {title || t("valai.recommendedTours")}
                    </h2>
                    <button
                        onClick={handleViewAll}
                        className="text-primary-color hover:text-primary-color-600 transition-colors"
                    >
                        {t('valai.viewAll')}
                    </button>
                </div>

                {/* Tour Cards Grid - Mobile: Horizontal Scroll, Desktop: Grid */}
                <div className={`md:grid md:grid-cols-2 ${provider === "victory" ? "lg:grid-cols-4" : "lg:grid-cols-3"} md:gap-6 mb-6 flex md:flex-none overflow-x-auto gap-4 pb-4 scroll-smooth snap-x snap-mandatory scrollbar-hide`}>
                    {/* Render Viator Tours */}
                    {provider === "viator" && viatorTours.map((tour) => {
                        const images = tour.images.map(img => {
                            const maxVariant = img.variants.reduce(
                                (max, curr) => (curr.width > max.width ? curr : max),
                                img.variants[0]
                            );
                            return maxVariant.url;
                        });

                        return (
                            <div
                                key={tour.productCode}
                                className="bg-white rounded-lg overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.08)] hover:shadow-[0_2px_12px_rgba(0,0,0,0.12)] transition-all duration-300 cursor-pointer flex-none w-[280px] md:w-auto snap-start"
                            >
                                {/* Tour Image */}
                                <div className="relative h-44">
                                    <img
                                        src={images[0] || "https://via.placeholder.com/400x200"}
                                        alt={tour.title}
                                        className="w-full h-full object-cover"
                                    />
                                </div>

                                {/* Tour Content */}
                                <div className="p-3">
                                    {/* Provider Logo */}
                                    <div className="flex items-center mb-2 justify-between">
                                        <div className="flex">
                                            <img
                                                src={viatorLogo}
                                                alt="Viator"
                                                className="w-5 h-5"
                                            />
                                            <span className="text-sm text-gray-500 ml-1.5">Viator</span>
                                        </div>

                                        {/* Rating */}
                                        {tour.reviews && (
                                            <div className="flex items-center mb-1.5">
                                                <span className="flex items-center">
                                                    <SvgIcons.Star className="w-3.5 h-3.5 text-[#FF5E1F]" />
                                                    <span className="text-sm font-medium text-gray-900 ml-1">
                                                        {tour.reviews.combinedAverageRating?.toFixed(1) || "N/A"}
                                                    </span>
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Tour Title */}
                                    <h3 className="text-base font-semibold text-gray-900 mb-1.5 leading-snug line-clamp-2">
                                        {tour.title}
                                    </h3>

                                    {/* Duration */}
                                    {tour.duration && (
                                        <div className="flex items-center mb-3">
                                            <SvgIcons.Clock2 className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-sm text-gray-400 ml-1.5">{tour.duration.fixedDurationInMinutes ? `${tour.duration.fixedDurationInMinutes} minutes` : "Duration varies"}</span>
                                        </div>
                                    )}

                                    {/* Price and Book Button */}
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="flex items-baseline">
                                                <span className="text-sm text-gray-900 font-medium mr-0.5">From</span>
                                                <span className="text-xl font-semibold text-gray-900">
                                                    {getCurrencySymbol(tour.pricing.currency)}{tour.pricing.summary.fromPrice?.toFixed(2) || "N/A"}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                if (onTourClick && cityId && cityName) {
                                                    const today = moment().format("YYYY-MM-DD");
                                                    const futureDate = moment().add(30, "days").format("YYYY-MM-DD");
                                                    onTourClick(Model.PROVIDER_ID.VIATOR, tour.productCode, {
                                                        cityId,
                                                        cityName,
                                                        startDate: today,
                                                        endDate: futureDate,
                                                        adults: 2,
                                                        children: 0,
                                                        url: tour.productUrl,
                                                    });
                                                } else {
                                                    window.open(tour.productUrl, "_blank");
                                                }
                                            }}
                                            className="tourBtn text-sm font-medium px-5 py-1.5 rounded-full transition-colors"
                                        >
                                            {t('trips.poi.book')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Render Tour API Products */}
                    {provider === "tourApi" && tourApiTours.map((tour) => {
                        const coverImage = tour.images.find(img => img.is_cover)?.url || tour.images[0]?.url;
                        const providerInfo = getProviderInfo(tour.providerId);

                        const formatDuration = (minutes: number): string => {
                            if (minutes < 60) return `${minutes} min`;
                            const hours = Math.floor(minutes / 60);
                            const remaining = minutes % 60;
                            return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
                        };

                        return (
                            <div
                                key={tour.productId}
                                className="bg-white rounded-lg overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.08)] hover:shadow-[0_2px_12px_rgba(0,0,0,0.12)] transition-all duration-300 cursor-pointer flex-none w-[280px] md:w-auto snap-start"
                            >
                                {/* Tour Image */}
                                {tour.providerId === Model.PROVIDER_ID.VICTORY ? (
                                    <div className="relative h-44 bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                                        <svg className="w-16 h-16 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                ) : (
                                    <div className="relative h-44">
                                        <img
                                            src={coverImage || "https://via.placeholder.com/400x200"}
                                            alt={tour.title}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                )}

                                {/* Tour Content */}
                                <div className="p-3">
                                    {/* Provider & Rating */}
                                    <div className="flex items-center mb-2 justify-between">
                                        <div className="flex items-center">
                                            {providerInfo.logo && (
                                                <img
                                                    src={providerInfo.logo}
                                                    alt={providerInfo.name}
                                                    className="w-5 h-5"
                                                />
                                            )}
                                            <span className={`text-sm text-gray-500 ${providerInfo.logo ? "ml-1.5" : ""}`}>{providerInfo.name}</span>
                                        </div>

                                        {tour.rating > 0 && (
                                            <div className="flex items-center mb-1.5">
                                                <span className="flex items-center">
                                                    <SvgIcons.Star className="w-3.5 h-3.5 text-[#FF5E1F]" />
                                                    <span className="text-sm font-medium text-gray-900 ml-1">
                                                        {tour.rating.toFixed(1)}
                                                    </span>
                                                    {tour.ratingCount > 0 && (
                                                        <span className="text-xs text-gray-400 ml-1">
                                                            ({tour.ratingCount})
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Tour Title */}
                                    <h3 className="text-base font-semibold text-gray-900 mb-1.5 leading-snug line-clamp-2">
                                        {tour.title}
                                    </h3>

                                    {/* Duration */}
                                    {tour.duration > 0 && (
                                        <div className="flex items-center mb-3">
                                            <SvgIcons.Clock2 className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-sm text-gray-400 ml-1.5">{formatDuration(tour.duration)}</span>
                                        </div>
                                    )}

                                    {/* Price and Book Button */}
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="flex items-baseline">
                                                <span className="text-sm text-gray-900 font-medium mr-0.5">From</span>
                                                <span className="text-xl font-semibold text-gray-900">
                                                    {getCurrencySymbol(tour.currency)}{tour.price.toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                if (onTourClick && cityId && cityName) {
                                                    onTourClick(tour.providerId, tour.productId, {
                                                        cityId,
                                                        cityName,
                                                        url: tour.url,
                                                    });
                                                } else {
                                                    window.open(tour.url, "_blank");
                                                }
                                            }}
                                            className="tourBtn text-sm font-medium px-5 py-1.5 rounded-full transition-colors"
                                        >
                                            {t('trips.poi.book')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Render Victory Events */}
                    {provider === "victory" && victoryEvents.map((event) => {
                        const tags: string[] = [];
                        if (event.category?.name) {
                            tags.push(event.category.name);
                        }
                        if (event.category?.parent?.name) {
                            tags.push(event.category.parent.name);
                        }

                        return (
                            <div
                                key={event.id}
                                className="bg-white rounded-lg overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.08)] hover:shadow-[0_2px_12px_rgba(0,0,0,0.12)] transition-all duration-300 cursor-pointer flex-none w-[280px] md:w-auto snap-start"
                            >
                                {/* Event Image - placeholder for now */}
                                <div className="relative h-44 bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                                    <svg className="w-16 h-16 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </div>

                                {/* Event Content */}
                                <div className="p-3">
                                    {/* Event Title */}
                                    <h3 className="text-base font-semibold text-gray-900 mb-1 leading-snug line-clamp-2">
                                        {event.name}
                                    </h3>

                                    {/* Event Time */}
                                    {event.occurs_at_local && (
                                        <p className="text-sm text-gray-500 mb-2">
                                            {formatEventTime(event.occurs_at_local)}
                                        </p>
                                    )}
                                    {/* Book Button */}
                                    <div className="flex items-center justify-end">
                                        <button
                                            onClick={() => {
                                                if (onTourClick) {
                                                    onTourClick(Model.PROVIDER_ID.VICTORY, event.id.toString(), {
                                                        url: event.url,
                                                    });
                                                } else {
                                                    window.open(event.url, "_blank");
                                                }
                                            }}
                                            className="tourBtn text-sm font-medium px-5 py-1.5 rounded-full transition-colors"
                                        >
                                            {t('trips.poi.book')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default RecommendTours;